import React, { useState, useEffect, useCallback } from 'react';
import { format, subDays } from 'date-fns';
import ReportLayout, { ReportTable, usePagination } from './ReportLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const fmtDT = v => { try { return format(new Date(v), 'd MMM yy h:mm a'); } catch { return v || '–'; } };
const currency = v => v != null ? `$${parseFloat(v).toFixed(2)}` : '–';
const statusBadge = v => {
  const map = { approved:'bg-green-100 text-green-700', pending:'bg-amber-100 text-amber-700', rejected:'bg-red-100 text-red-700' };
  return <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium capitalize', map[v] || 'bg-slate-100 text-slate-600')}>{v || '–'}</span>;
};

const COLS = [
  { key: 'shift_date',       label: 'Shift Date' },
  { key: 'staff_name',       label: 'Staff Name' },
  { key: 'client_names',     label: 'Client(s)' },
  { key: 'pay_group',        label: 'Pay Group' },
  { key: 'shift_type',       label: 'Shift Type' },
  { key: 'clock_in_time',    label: 'Clock In',   render: fmtDT },
  { key: 'clock_out_time',   label: 'Clock Out',  render: fmtDT },
  { key: 'hours_worked',     label: 'Hours',      render: v => v ? `${parseFloat(v).toFixed(2)}h` : '–' },
  { key: 'pay_band',         label: 'Pay Band' },
  { key: 'pay_multiplier',   label: 'Multiplier', render: v => v ? `${parseFloat(v).toFixed(2)}x` : '–' },
  { key: 'pay_amount',       label: 'Pay Amount', render: currency },
  { key: 'mileage_km',       label: 'KM',         render: v => v ? `${parseFloat(v).toFixed(1)} km` : '–' },
  { key: 'expense',          label: 'Expense',    render: currency },
  { key: 'timesheet_status', label: 'Status',     render: statusBadge },
];

export default function TimesheetPayRates() {
  const [data, setData]         = useState([]);
  const [staff, setStaff]       = useState([]);
  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [startDate, setStart]   = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEnd]       = useState(format(new Date(), 'yyyy-MM-dd'));
  const [staffId, setStaffId]   = useState('');
  const [clientId, setClientId] = useState('');
  const [status, setStatus]     = useState('');
  const pagination = usePagination(data);

  useEffect(() => {
    api.get('/staff').then(r => setStaff(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/clients').then(r => setClients(Array.isArray(r.data) ? r.data : r.data?.clients || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (staffId)  p.set('staff_id', staffId);
    if (clientId) p.set('client_id', clientId);
    if (status)   p.set('status', status);
    api.get(`/reports/timesheet-pay-rates?${p}`)
      .then(r => { setData(r.data || []); pagination.reset(); })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, staffId, clientId, status]);

  useEffect(() => { load(); }, [load]);

  const totals = data.reduce((acc, r) => ({
    hours:  acc.hours  + parseFloat(r.hours_worked || 0),
    pay:    acc.pay    + parseFloat(r.pay_amount   || 0),
    km:     acc.km     + parseFloat(r.mileage_km   || 0),
    expense:acc.expense+ parseFloat(r.expense      || 0),
  }), { hours: 0, pay: 0, km: 0, expense: 0 });

  const selStaff  = staff.find(s => s.id === staffId);
  const selClient = clients.find(c => c.id === clientId);
  const chips = [
    { label: 'Date Range', value: `${startDate} — ${endDate}` },
    ...(selStaff  ? [{ label: 'Staff',  value: `${selStaff.first_name} ${selStaff.last_name}`,   onRemove: () => setStaffId('') }]  : []),
    ...(selClient ? [{ label: 'Client', value: `${selClient.first_name} ${selClient.last_name}`, onRemove: () => setClientId('') }] : []),
    ...(status    ? [{ label: 'Status', value: status, onRemove: () => setStatus('') }] : []),
  ];

  return (
    <ReportLayout title="Timesheet Report With Pay Rates" data={data} filename="timesheet_pay_rates"
      columns={COLS} activeChips={chips} showFiltersPanel={showFilters} onToggleFilters={() => setShowFilters(v => !v)}
      filters={
        <div className="flex flex-wrap gap-4">
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={e => setStart(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={e => setEnd(e.target.value)} className="input-field !w-auto text-sm" /></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Staff</label>
            <select value={staffId} onChange={e => setStaffId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Staff</option>
              {staff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Client</label>
            <select value={clientId} onChange={e => setClientId(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Clients</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-500 mb-1">Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} className="input-field !w-auto text-sm">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select></div>
          <div className="flex items-end">
            <button onClick={load} className="btn-primary text-sm px-4 py-2">Apply</button>
          </div>
        </div>
      }>
      {data.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-4">
          {[
            { label: 'Total Hours',   value: `${totals.hours.toFixed(2)}h` },
            { label: 'Total Pay',     value: currency(totals.pay) },
            { label: 'Total KM',      value: `${totals.km.toFixed(1)} km` },
            { label: 'Total Expenses',value: currency(totals.expense) },
          ].map(card => (
            <div key={card.label} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500 mb-1">{card.label}</div>
              <div className="text-xl font-bold text-slate-900">{card.value}</div>
            </div>
          ))}
        </div>
      )}
      <ReportTable columns={COLS} data={data} loading={loading} pagination={pagination} />
    </ReportLayout>
  );
}
